import { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { usePasswordCompare } from "hooks/use-password-compare";
import { useTranslation } from "hooks/use-translation";
import PasswordInput from "components/password-input";
import Input from "components/input";
import Button from "components/button";
import { MainWrapper } from "components/main-wrapper";
import { usePasswordCriteria } from "hooks/use-password-criteria";
import PhoneInput from "components/phone-input";
import { isPossiblePhoneNumber } from "react-phone-number-input";
import { useLoginCheck } from "hooks/use-login-check";
import { useStateSearchParam } from "hooks/use-state-search-param";

const { REACT_APP_SSO_DOMAIN } = process.env;

// the only query params the worker's /login accepts
const LOGIN_QUERY_PARAMS = ["redirect_uri", "state", "disable_sign_up"];

export function Register() {
  const { tpage, tcommon } = useTranslation({
    page: "register",
  });

  const searchParams = useMemo(
    () => new URLSearchParams(window.location.search),
    [window.location.search],
  );

  const [fields, setFields] = useState({
    email: searchParams.get("email") ?? "",
    password: "",
    confirmedPassword: "",
    code: "",
    phoneNumber: "",
  });

  const {
    isPasswordLogin,
    loading: loginCheckLoading,
    onFormSubmit: onEmailFormSubmit,
  } = useLoginCheck(fields.email);

  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(tpage("error"));
  const [emailConfirmError, setEmailConfirmError] = useState(false);
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const [finished, setFinished] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [loading, setLoading] = useState(false);

  const { state } = useStateSearchParam();
  const loginForm = useRef(null);

  const loginQuery = useMemo(() => {
    const params = new URLSearchParams();

    for (const key of LOGIN_QUERY_PARAMS) {
      const value = searchParams.get(key);
      if (value) params.set(key, value);
    }

    /**
     * Same fallback as goToLogin (ENG-858), so users still end up
     * in the installer app when no redirect_uri was provided
     */
    if (!params.has("redirect_uri")) {
      params.set("redirect_uri", "cloud.givenergy.installer://");
    }

    return `?${params}`;
  }, [searchParams]);

  // hands over to the normal login flow (cookie, redirect_uri, state, ...)
  const logIn = () => {
    setFinished(true);
    setLoggingIn(true);
    loginForm.current?.submit();
  };

  const [renderPasswordCriteria, validPassword, setPasswordForCriteria] =
    usePasswordCriteria();

  const isLoading = loading || loginCheckLoading;
  const phoneNumberIsValid =
    !fields.phoneNumber || isPossiblePhoneNumber(fields.phoneNumber);

  const [disableSignUpBtn, setDisableSignUpBtn] = useState(false);

  const passwordConfirm = useRef(null);

  const unverified = searchParams.get("unverified");

  useEffect(() => {
    if (unverified) {
      setFields({
        email: searchParams.get("email"),
      });
      setNeedsConfirmation(true);
    }
  }, [searchParams]);

  const register = async (event) => {
    event.preventDefault();

    if (validPassword == false) return;

    if (phoneNumberIsValid == false) return;

    setDisableSignUpBtn(true);
    setLoading(true);

    try {
      const response = await fetch(`${REACT_APP_SSO_DOMAIN}/register`, {
        method: "POST",
        body: JSON.stringify(fields),
      });

      if (response.ok == false) {
        const text = await response.text();
        let errorMessage = "";

        if (text.includes("UsernameExistsException")) {
          errorMessage = tpage("account-exists-error");
        } else if (
          text.includes("missing phoneNumber") ||
          text.includes("invalid phone number format")
        ) {
          errorMessage = tpage("phone-number-error");
        } else {
          errorMessage = tpage("error");
        }

        setErrorMessage(errorMessage);
      }

      // new accounts may be auto-confirmed, so there's no code to enter
      const { confirmed } = response.ok
        ? await response.json().catch(() => ({}))
        : {};

      setError(response.ok == false);

      if (response.ok && confirmed === true) {
        logIn();
        return;
      }

      setNeedsConfirmation(response.ok);
    } catch {
      setError(true);
      setNeedsConfirmation(false);
    } finally {
      setLoading(false);
    }

    setDisableSignUpBtn(false);
  };

  const handleConfirmEmail = async (event) => {
    event.preventDefault();

    setLoading(true);

    try {
      const response = await fetch(`${REACT_APP_SSO_DOMAIN}/confirm`, {
        method: "POST",
        body: JSON.stringify(fields),
      });
      setEmailConfirmError(response.ok == false);

      // the password is only known if they registered in this session
      if (response.ok && fields.password) {
        logIn();
        return;
      }

      setFinished(response.ok);
    } catch {
      setEmailConfirmError(true);
      setFinished(false);
    } finally {
      setLoading(false);
    }
  };

  const goToLogin = async (event) => {
    event.preventDefault();

    let params = window.localStorage.getItem("loginSearchParams") || "";

    /**
     * Tempory fix for ENG-858, remove after completion of ticket
     *  - Jamie M
     */
    if (!params) {
      params = "?redirect_uri=cloud.givenergy.installer://";
    }

    const urlParams = new URLSearchParams(params);
    urlParams.delete("email");
    urlParams.delete("unverified");
    location.href = `/login${params}`;
  };

  usePasswordCompare({ passwordConfirm }, fields);

  useEffect(() => {
    window.localStorage.setItem("loginSearchParams", window.location.search);
  }, []);

  if (!isPasswordLogin && !needsConfirmation && !finished) {
    return (
      <MainWrapper
        description={tpage("get-started-description")}
      >
        <form onSubmit={onEmailFormSubmit} className="space-y-4">
          <Input
            label={tpage("email-address")}
            id="email"
            type="email"
            name="email"
            value={fields.email}
            placeholder="new.user@givenergy.co.uk"
            onChange={(event) =>
              setFields({ ...fields, email: event.target.value })
            }
            required
          />

          <Button type="submit" loading={isLoading}>
            {tpage("next")}
          </Button>

          <div className="flex justify-center gap-1">
            <p className="text-xs">{tpage("login-cta")}</p>
            <a
              href={`/login${
                window.localStorage.getItem("loginSearchParams") ?? ""
              }`}
            >
              {tpage("login")}
            </a>
          </div>
        </form>
      </MainWrapper>
    );
  }

  return (
    <MainWrapper
      description={tpage("get-started-description")}
    >
      <form onSubmit={register} className="space-y-4">
        {needsConfirmation && finished == false && (
          <span className="text-center">
            {tpage("confirmation")}: {fields.email}
          </span>
        )}

        {finished && (
          <p className="text-center">
            {tpage(loggingIn ? "logging-in" : "completed")}
          </p>
        )}

        <Input
          className={clsx((needsConfirmation || finished) && "hidden")}
          label={tcommon("email")}
          id="email"
          type="email"
          name="email"
          value={fields.email}
          placeholder="new.user@givenergy.co.uk"
          onChange={(event) =>
            setFields({ ...fields, email: event.target.value })
          }
          required
        />

        <PhoneInput
          className={clsx((needsConfirmation || finished) && "hidden")}
          label={tpage("phone-number-optional")}
          required={false}
          value={fields.phoneNumber}
          setValue={(value) => setFields({ ...fields, phoneNumber: value })}
        />

        {needsConfirmation && finished === false && (
          <>
            <Input
              label={tcommon("code")}
              id="code"
              name="code"
              value={fields.code}
              onChange={(event) =>
                setFields({ ...fields, code: event.target.value })
              }
              required
            />
            {emailConfirmError && (
              <p className="max-w-full text-red-500">{tpage("invalid-code")}</p>
            )}
          </>
        )}

        {!needsConfirmation && finished === false && (
          <>
            <PasswordInput
              label={tcommon("password")}
              id="password"
              name="password"
              value={fields.password}
              placeholder="Password"
              onChange={(event) => {
                setFields({ ...fields, password: event.target.value });
                setPasswordForCriteria(event.target.value);
              }}
              required
            />

            <PasswordInput
              ref={passwordConfirm}
              label={tcommon("confirm")}
              id="password-confirm"
              name="confirmedPassword"
              value={fields.confirmedPassword}
              placeholder="Confirm Password"
              onChange={(event) =>
                setFields({ ...fields, confirmedPassword: event.target.value })
              }
              required
            />

            {renderPasswordCriteria()}

            {error && <p className="max-w-full text-red-500">{errorMessage}</p>}
          </>
        )}

        {finished && !loggingIn && (
          <Button onClick={goToLogin}>{tpage("go-to-login")}</Button>
        )}

        {finished === false && (
          <div className="flex justify-end">
            {needsConfirmation ? (
              <Button loading={isLoading} onClick={handleConfirmEmail}>
                {tcommon("confirm")}
              </Button>
            ) : (
              <Button
                disable={disableSignUpBtn ? "true" : "false"}
                type="submit"
                loading={isLoading}
              >
                {tpage("sign-up")}
              </Button>
            )}
          </div>
        )}
      </form>

      <form
        ref={loginForm}
        action={`${REACT_APP_SSO_DOMAIN}/login${loginQuery}`}
        method="POST"
        encType="multipart/form-data"
        className="hidden"
      >
        <input type="hidden" name="email" value={fields.email ?? ""} />
        <input type="hidden" name="password" value={fields.password ?? ""} />
        <input type="hidden" name="device" value={state?.device ?? ""} />
      </form>

      {!needsConfirmation && !finished && (
        <div className="mt-4 flex justify-center gap-1">
          <p className="text-xs">{tpage("login-cta")}</p>
          <a
            href={`/login${
              window.localStorage.getItem("loginSearchParams") ?? ""
            }`}
          >
            {tpage("login")}
          </a>
        </div>
      )}
    </MainWrapper>
  );
}
